<script lang="ts">
  import { createQuery } from '@tanstack/svelte-query';
  import { hrAuditApi, hrUsersApi } from '$lib/api/hr';
  import { AUDIT_ACTION_LABEL, humanizeAuditDetails } from '$lib/utils/audit';
  import Toolbar from '$lib/components/ui/Toolbar.svelte';
  import DataTable from '$lib/components/ui/DataTable.svelte';
  import StatusBadge from '$lib/components/ui/StatusBadge.svelte';
  import type { AuditLogRow } from '$lib/types';

  let userFilter = $state('');
  let actionFilter = $state('');

  const query = createQuery(() => ({
    queryKey: ['admin-audit', userFilter, actionFilter],
    queryFn: () => hrAuditApi.adminList({ user_id: userFilter, action: actionFilter })
  }));
  const usersQuery = createQuery(() => ({
    queryKey: ['hr-users'],
    queryFn: () => hrUsersApi.list()
  }));

  function fmt(iso: string): string {
    return new Date(iso).toLocaleString();
  }
</script>

<div class="tym-page">
  <p class="tym-section-sub mb-4">Every HR/admin correction and automated attendance-integrity action, newest first.</p>
  <Toolbar>
    {#snippet filters()}
      <select class="input" aria-label="Target user" bind:value={userFilter}>
        <option value="">All users</option>
        {#each usersQuery.data ?? [] as u (u.id)}
          <option value={String(u.id)}>{u.name || u.email}</option>
        {/each}
      </select>
      <select class="input" aria-label="Action" bind:value={actionFilter}>
        <option value="">All actions</option>
        {#each Object.entries(AUDIT_ACTION_LABEL) as [value, label] (value)}
          <option {value}>{label}</option>
        {/each}
      </select>
    {/snippet}
  </Toolbar>

  {#if query.isLoading}
    <div class="tym-loading-state">Loading...</div>
  {:else if query.isError}
    <div class="tym-error-state">Could not load the audit log.</div>
  {:else if query.data}
    <DataTable
      mobileCards
      mobileColumns={['action', 'when', 'actor', 'target', 'details']}
      columns={[
        { key: 'when', label: 'When', cell: whenCell },
        { key: 'actor', label: 'Actor', cell: actorCell },
        { key: 'target', label: 'Target', cell: targetCell },
        { key: 'action', label: 'Action', cell: actionCell },
        { key: 'details', label: 'Details', cell: detailsCell }
      ]}
      rows={query.data}
      rowKey={(row) => row.id}
      emptyTitle="No audit activity yet"
      emptyBody="HR corrections and automatic attendance-integrity actions will show up here as they happen."
    />
  {/if}
</div>

{#snippet whenCell(row: AuditLogRow)}
  <span class="muted" title={fmt(row.created_at)}>{fmt(row.created_at)}</span>
{/snippet}
{#snippet actorCell(row: AuditLogRow)}
  <span class="muted">{row.actor_name || (row.actor_user_id ? `#${row.actor_user_id}` : 'System')}</span>
{/snippet}
{#snippet targetCell(row: AuditLogRow)}
  <span class="font-medium text-navy">{row.target_name || `#${row.target_user_id}`}</span>
{/snippet}
{#snippet actionCell(row: AuditLogRow)}
  <StatusBadge kind="info" label={AUDIT_ACTION_LABEL[row.action] ?? row.action} />
{/snippet}
{#snippet detailsCell(row: AuditLogRow)}
  <span class="muted">{humanizeAuditDetails(row.action, row.details)}</span>
{/snippet}
